import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { SwitchProps, useSwitch } from '@heroui/switch';
import { useTheme } from '@heroui/use-theme';

import { SunFilledIcon, MoonFilledIcon } from '@/components/icons';
import { cn } from '@/utils/helpers';

export interface ThemeSwitchProps {
  className?: string;
  classNames?: SwitchProps['classNames'];
}

export const ThemeSwitch: FC<ThemeSwitchProps> = ({ className, classNames }) => {
  const [isMounted, setIsMounted] = useState(false);

  const { theme, setTheme } = useTheme();

  const { Component, isSelected, getBaseProps, getInputProps, getWrapperProps } = useSwitch({
    isSelected: theme === 'light',
    onChange: () => setTheme(theme === 'light' ? 'dark' : 'light')
  });

  useEffect(() => {
    setIsMounted(true);
  }, [isMounted]);

  // Prevent Hydration Mismatch
  if (!isMounted) return <div className="h-6 w-6" />;

  return (
    <Component
      aria-label={isSelected ? 'Switch to dark mode' : 'Switch to light mode'}
      {...getBaseProps({
        className: cn('px-px transition-opacity hover:opacity-80 cursor-pointer', className, classNames?.base)
      })}
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <div
        {...getWrapperProps()}
        className={cn(
          [
            'h-auto w-auto',
            'bg-transparent',
            'rounded-lg',
            'flex items-center justify-center',
            'group-data-[selected=true]:bg-transparent',
            '!text-default-500',
            'pt-px',
            'px-0',
            'mx-0'
          ],
          classNames?.wrapper
        )}
      >
        {isSelected ? <MoonFilledIcon size={22} /> : <SunFilledIcon size={22} />}
      </div>
    </Component>
  );
};
